import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import Header from "@/components/Header";
import { usePricing, RecruitingCheckoutButton } from "@/components/Pricing";
import { useAuth } from "@/lib/auth";

/* Public pricing page (/pricing) — also served at /upgrade for signed-in users.
   Every price on this page comes from /api/meta/pricing (backend/pricing.py). */
export default function Pricing() {
  const pricing = usePricing();
  const { user } = useAuth();
  const plans = pricing?.plans || [];
  const accentFor = (p) => (p.audience === "coach" ? "volt" : "blaze");

  return (
    <div className="min-h-screen bg-appbg text-white">
      <Header />
      <div className="px-4 md:px-8 lg:px-12 py-10 max-w-6xl" data-testid="pricing-page">
        <div className="text-xs tracking-widest text-blaze">PRICING · SIMPLE · CANCEL ANYTIME</div>
        <h1 className="heading text-4xl sm:text-5xl lg:text-6xl mt-2">COACHES FREE. <span className="text-blaze">PROGRAMS RECRUIT FOR $5.</span></h1>
        <p className="text-neutral-400 mt-3 max-w-3xl text-base md:text-lg" data-testid="pricing-offer-line">{pricing?.offer_line}</p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-800 border border-neutral-800">
          {plans.map((p) => (
            <div key={p.key} className={`p-8 ${p.key === "program_recruiting" ? "bg-appcard" : "bg-appbg"}`} data-testid={`plan-${p.key}`}>
              <div className={`text-xs tracking-widest text-${accentFor(p)}`}>{p.label.toUpperCase()}</div>
              <div className="heading mt-3 flex items-end gap-2">
                <span className="text-5xl" data-testid={`plan-${p.key}-price`}>{p.price}</span>
                {p.per && <span className="text-sm text-neutral-500 mb-2">{p.per}</span>}
              </div>
              <ul className="mt-6 space-y-2">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-neutral-300">
                    <Check className={`text-${accentFor(p)} mt-0.5 shrink-0`} size={16} /><span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                {p.key === "coach_free" && (
                  <Link to={user?.role === "coach" ? "/coach" : "/coaches/find"} data-testid="plan-coach-cta"
                    className="inline-flex items-center gap-2 bg-volt text-black heading text-sm px-5 py-3 hover:brightness-110 transition-all">
                    {user?.role === "coach" ? "GO TO MY DASHBOARD →" : "CLAIM MY FREE PROFILE →"}
                  </Link>
                )}
                {p.key === "program_basic" && (
                  <Link to={user?.role === "athletic_director" ? "/ad/jobs/new" : "/post-job"} data-testid="plan-basic-cta"
                    className="inline-flex items-center gap-2 bg-neutral-800 text-white heading text-sm px-5 py-3 hover:bg-neutral-700 transition-all">
                    POST A JOB — FREE →
                  </Link>
                )}
                {p.key === "program_recruiting" && <RecruitingCheckoutButton />}
              </div>
            </div>
          ))}
        </div>
        {pricing?.fallback && <div className="text-[11px] text-neutral-500 mt-4" data-testid="pricing-fallback-note">Showing standard pricing (live pricing service unavailable).</div>}
        <p className="text-xs text-neutral-500 mt-8 max-w-3xl" data-testid="pricing-footnote">
          Program Recruiting is billed per program at {plans.find((p) => p.key === "program_recruiting")?.price || "$5/month"} in USD via Stripe. Existing Founding Coach and founding-program recognition is preserved. Coach privacy controls are never tied to payment.
        </p>
      </div>
    </div>
  );
}
